import React, { ReactNode } from 'react';

import IconButton from '@atlaskit/button/icon/button';
import AkHeading from '@atlaskit/heading/heading';
import ArrowRightIcon from '@atlaskit/icon/core/arrow-right';
import { Inline } from '@atlaskit/primitives/compiled/inline';
import { Text } from '@atlaskit/primitives/compiled/text';
import { token } from '@atlaskit/tokens';

import { useAnalytics } from '@townsquare/analytics';
import { Link, type LinkProps } from '@townsquare/router/primitives/link';
import { fg } from '@townsquare/stat-sig/gate';
import { useMetaClickHandler } from '@townsquare/ui-interactions';

type BrowseMoreLinkProps = Pick<LinkProps, 'to' | 'params' | 'query'> & { onClick?: () => void };

interface HeadingProps {
	children: ReactNode;
	screenType: string;
	onClick?: () => void;
	browseMore?: {
		text: string;
		linkProps: BrowseMoreLinkProps;
	};
}

export const Heading = ({ children, browseMore, screenType }: HeadingProps): React.JSX.Element => {
	return (
		<Inline alignBlock="center" space="space.100" spread="space-between" shouldWrap>
			<AkHeading size="small" as="h2">
				{children}
			</AkHeading>
			{browseMore && (
				<AsLink
					screenType={screenType}
					browseMoreText={browseMore.text}
					linkProps={browseMore.linkProps}
				/>
			)}
		</Inline>
	);
};

const AsLink = ({
	screenType,
	browseMoreText,
	linkProps,
}: {
	screenType: string;
	browseMoreText: string;
	linkProps: BrowseMoreLinkProps;
}) => {
	const analytics = useAnalytics();

	const { handler } = useMetaClickHandler(
		linkProps.to,
		{
			params: linkProps.params,
			query: linkProps.query,
		},
		() => {
			analytics.ui('staffDirectoryBrowseAllScreenHeading', 'clicked', { screenType });
			linkProps.onClick?.();
		},
	);

	return (
		// eslint-disable-next-line @atlassian/a11y/click-events-have-key-events, @atlassian/a11y/no-static-element-interactions
		<Link {...linkProps} onClick={handler}>
			<Inline alignBlock="center" space="space.100">
				<Text color="color.text.subtlest">{browseMoreText}</Text>
				{fg('teams-a11y-35584-35469-35464') ? (
					<ArrowRightIcon label="" color={token('color.text.subtlest')} />
				) : (
					<IconButton icon={ArrowRightIcon} label="" spacing="compact" />
				)}
			</Inline>
		</Link>
	);
};
